import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Optional,
} from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * Auto-trims leading/trailing whitespace on blur for all text fields.
 *
 * Auto-applies to: native inputs (text/email/search/tel/url), textarea,
 *                  custom-text-field, custom-text-area-field
 * Opt-out: add [sbiTrimDisabled] to exclude a specific field
 */
@Directive({
  selector: `
    input[type=text]:not([sbiTrimDisabled]),
    input[type=email]:not([sbiTrimDisabled]),
    input[type=search]:not([sbiTrimDisabled]),
    input[type=tel]:not([sbiTrimDisabled]),
    input[type=url]:not([sbiTrimDisabled]),
    input:not([type]):not([sbiTrimDisabled]),
    textarea:not([sbiTrimDisabled]),
    custom-text-field:not([sbiTrimDisabled]),
    custom-text-area-field:not([sbiTrimDisabled]),
    [sbiTrimOnBlur]
  `,
  standalone: true,
})
export class TrimOnBlurDirective implements AfterViewInit, OnDestroy {
  private _field: HTMLInputElement | HTMLTextAreaElement | null = null;

  private readonly _onBlur = (): void => {
    if (!this._field) return;

    const rawValue = this._field.value;
    if (typeof rawValue !== 'string') return;

    const trimmed = rawValue.trim();
    if (trimmed === rawValue) return;

    this._field.value = trimmed;

    if (this._ngControl?.control) {
      this._ngControl.control.setValue(trimmed, { emitEvent: true });
      this._ngControl.control.markAsDirty();
    }
  };

  constructor(
    private readonly _host: ElementRef<HTMLElement>,
    @Optional() private readonly _ngControl: NgControl
  ) {}

  ngAfterViewInit(): void {
    const nativeEl = this._host.nativeElement;
    const tag = nativeEl.tagName;

    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      this._field = nativeEl as HTMLInputElement | HTMLTextAreaElement;
    } else {
      this._field = nativeEl.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >('input, textarea');
    }

    // Skip password fields regardless of how the type was bound (static or dynamic)
    if (
      this._field instanceof HTMLInputElement &&
      this._field.type === 'password'
    ) {
      this._field = null;
      return;
    }

    this._field?.addEventListener('blur', this._onBlur);
  }

  ngOnDestroy(): void {
    this._field?.removeEventListener('blur', this._onBlur);
  }
}
